import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const APPLE_EULA_URL = "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/";

export function TermsLinks({ className = "", testidPrefix = "terms" }) {
  const cls = "text-rose-400 hover:text-rose-300 underline underline-offset-2";
  return (
    <span className={className}>
      <Link to="/legal/terms-of-service" target="_blank" rel="noreferrer" className={cls} data-testid={`${testidPrefix}-link-terms`}>Terms of Use (EULA)</Link>,{" "}
      <Link to="/legal/privacy-policy" target="_blank" rel="noreferrer" className={cls} data-testid={`${testidPrefix}-link-privacy`}>Privacy Policy</Link> and{" "}
      <Link to="/legal/acceptable-use-and-content-policy" target="_blank" rel="noreferrer" className={cls} data-testid={`${testidPrefix}-link-content-policy`}>Content Policy</Link>
    </span>
  );
}

export const TERMS_CHECKBOX_COPY = (
  <>I am 18 or older and I agree to the <TermsLinks testidPrefix="register" />. I understand My Panda X has <strong className="text-zinc-200">zero tolerance</strong> for objectionable content, abusive behavior, and explicit sexual acts — violators are removed.</>
);

export default function TermsAcceptanceGate() {
  const { user, setUser, refresh } = useAuth();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const needsProbe = !!user && user.terms_required === undefined;
  useEffect(() => { if (needsProbe) refresh(); }, [needsProbe, refresh]);
  if (!user || !user.terms_required) return null;

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/accept-terms", { version: user.terms_current_version });
      const { data } = await api.get("/auth/me");
      setUser(data);
      toast.success("Thanks — you're all set.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your acceptance.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent className="bg-zinc-950 border-white/10 text-white rounded-none max-w-md [&>button]:hidden" data-testid="terms-gate" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 grid place-items-center bg-rose-600/15 border border-rose-500/40 text-rose-300"><ShieldCheck size={20} /></span>
            <DialogTitle className="font-display text-2xl">Before you continue</DialogTitle>
          </div>
          <DialogDescription className="text-sm text-zinc-400 pt-2">
            {user.terms_version ? "Our terms were updated. " : ""}Please review and accept our community rules to keep using My Panda X.
          </DialogDescription>
        </DialogHeader>
        <ul className="text-sm text-zinc-300 space-y-2 border-l-2 border-rose-600/60 pl-4">
          <li>18+ only. No explicit sexual acts or behavior, nudity, or pornography anywhere on the platform.</li>
          <li>Zero tolerance for harassment, hate, threats, non-consensual or illegal content.</li>
          <li>Report anything objectionable — we review every report within 24 hours and remove content or eject the offender.</li>
        </ul>
        <label className="flex items-start gap-3 cursor-pointer mt-2">
          <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="terms-gate-checkbox" className="mt-1 border-white/30 data-[state=checked]:bg-rose-600 data-[state=checked]:border-rose-600" />
          <span className="text-sm text-zinc-300 leading-relaxed">I have read and agree to the <TermsLinks testidPrefix="terms-gate" />.</span>
        </label>
        <div className="flex items-center justify-between gap-3 pt-2">
          <a href={APPLE_EULA_URL} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-white underline underline-offset-2" data-testid="terms-gate-link-apple-eula">Apple Standard EULA</a>
          <Button disabled={!checked || busy} onClick={accept} data-testid="terms-gate-accept" className="rounded-none bg-rose-600 hover:bg-rose-500 text-white px-6">
            {busy ? "Saving…" : "Agree & continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
